import axios from 'axios';
import { rbacApi } from '../config/api';
import { errorHandler } from '../utils/errorHandler';

const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:8000/api/v1' : 'https://datahub.n2nai.io/api/v1');

// RBAC API Service - Updated to use unified API configuration
class RBACApiService {
    // Permission Management
    static async getPermissions(category = null) {
        try {
            return await rbacApi.getPermissions(category);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'getPermissions',
                category
            });
            throw parsedError;
        }
    }

    static async createPermission(permission) {
        try {
            return await rbacApi.createPermission(permission);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'createPermission',
                permission
            });
            throw parsedError;
        }
    }

    static async updatePermission(permissionId, permission) {
        try {
            return await rbacApi.put(`/permissions/${permissionId}`, permission);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'updatePermission',
                permissionId,
                permission
            });
            throw parsedError;
        }
    }

    static async deletePermission(permissionId) {
        try {
            return await rbacApi.delete(`/permissions/${permissionId}`);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'deletePermission',
                permissionId
            });
            throw parsedError;
        }
    }

    // Role Management
    static async getRoles(organizationId = null) {
        try {
            return await rbacApi.getRoles(organizationId);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'getRoles',
                organizationId
            });
            throw parsedError;
        }
    }

    static async createRole(role) {
        try {
            return await rbacApi.createRole(role);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'createRole',
                role
            });
            throw parsedError;
        }
    }

    static async updateRole(roleId, role) {
        try {
            return await rbacApi.put(`/roles/${roleId}`, role);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'updateRole',
                roleId,
                role
            });
            throw parsedError;
        }
    }

    static async deleteRole(roleId) {
        try {
            return await rbacApi.delete(`/roles/${roleId}`);
        } catch (error) {
            const parsedError = errorHandler.handle(error, {
                context: 'deleteRole',
                roleId
            });
            throw parsedError;
        }
    }

    // User Management
    static async getUsers(organizationId = null) {
        try {
            const params = organizationId ? { organization_id: organizationId } : {};
            const headers = this.getAuthHeaders();
            const response = await axios.get(`${API_BASE_URL}/rbac/users`, {
                params,
                headers
            });
            return response.data;
        } catch (error) {
            console.error('Error getting users:', error);
            throw error;
        }
    }

    static async createUser(user) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.post(`${API_BASE_URL}/rbac/users`, user, { headers });
            return response.data;
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    }

    static async updateUser(userId, user) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.put(`${API_BASE_URL}/rbac/users/${userId}`, user, { headers });
            return response.data;
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }

    static async deleteUser(userId) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.delete(`${API_BASE_URL}/rbac/users/${userId}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    }

    // User-Role Management
    static async getUserPermissions(userId) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.get(`${API_BASE_URL}/rbac/users/${userId}/permissions`, { headers });
            return response.data.permissions;
        } catch (error) {
            console.error('Error getting user permissions:', error);
            throw error;
        }
    }

    static async assignRoleToUser(userId, roleId) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.post(`${API_BASE_URL}/rbac/users/${userId}/roles/${roleId}`, {}, { headers });
            return response.data;
        } catch (error) {
            console.error('Error assigning role to user:', error);
            throw error;
        }
    }

    static async removeRoleFromUser(userId, roleId) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.delete(`${API_BASE_URL}/rbac/users/${userId}/roles/${roleId}`, { headers });
            return response.data;
        } catch (error) {
            console.error('Error removing role from user:', error);
            throw error;
        }
    }

    // Permission Checking
    static async checkPermission(userId, resource, action, organizationId = null) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.post(
                `${API_BASE_URL}/rbac/check-permission`,
                {
                    user_id: userId,
                    resource,
                    action,
                    organization_id: organizationId
                },
                { headers }
            );
            return response.data.has_permission;
        } catch (error) {
            console.error('Error checking permission:', error);
            throw error;
        }
    }

    // RBAC Utilities
    static async getUserContext(userId, organizationId = null) {
        try {
            const [user, permissions] = await Promise.all([
                this.getUsers(organizationId).then((users) => users.find((u) => u.id === userId)),
                this.getUserPermissions(userId)
            ]);

            return {
                user,
                permissions,
                organization_id: organizationId
            };
        } catch (error) {
            console.error('Error getting user context:', error);
            throw error;
        }
    }

    static async hasPermission(userId, resource, action, organizationId = null) {
        try {
            return await this.checkPermission(userId, resource, action, organizationId);
        } catch (error) {
            console.error('Error checking permission:', error);
            return false;
        }
    }

    static async hasRole(userId, roleName, organizationId = null) {
        try {
            const user = await this.getUsers(organizationId).then((users) => users.find((u) => u.id === userId));
            return user && user.default_role === roleName;
        } catch (error) {
            console.error('Error checking role:', error);
            return false;
        }
    }

    static async getEffectivePermissions(userId, organizationId = null) {
        try {
            return await this.getUserPermissions(userId);
        } catch (error) {
            console.error('Error getting effective permissions:', error);
            return [];
        }
    }

    // Migration Support
    static async migrateData(migrationData) {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.post(`${API_BASE_URL}/rbac/migrate`, migrationData, { headers });
            return response.data;
        } catch (error) {
            console.error('Error migrating data:', error);
            throw error;
        }
    }

    // Health Check
    static async healthCheck() {
        try {
            const headers = this.getAuthHeaders();
            const response = await axios.get(`${API_BASE_URL}/rbac/health`, { headers });
            return response.data;
        } catch (error) {
            console.error('RBAC API health check failed:', error);
            throw error;
        }
    }
}

export default RBACApiService;
